import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import clx from 'classnames';
import { ChevronUp, ChevronDown } from 'lucide-react';
import styles from './TimeStepper.module.scss';

const MAX_MINUTES = 23 * 60 + 55;
const DEFAULT_MINUTE_STEP = 5;

export const pad2 = (value) => String(Math.max(0, Number(value) || 0)).padStart(2, '0');

/** iOS often does not replace the selection: "11" + "4" becomes "114" (or is blocked by maxLength). */
export const nextTimeDraft = (incomingRaw, previousDraft, { justFocused = false, maxLen = 2 } = {}) => {
  const incoming = String(incomingRaw ?? '').replace(/\D/g, '');
  const prev = String(previousDraft ?? '').replace(/\D/g, '');
  if (justFocused && incoming.length > prev.length && incoming.startsWith(prev)) {
    return incoming.slice(prev.length).slice(0, maxLen);
  }
  return incoming.slice(0, maxLen);
};

export const minutesOf = (value) => dayjs(value).hour() * 60 + dayjs(value).minute();

export const snapToStep = (value, step = DEFAULT_MINUTE_STEP) => {
  if (!step || step <= 1) return value;
  return Math.round(value / step) * step;
};

export const atMinutes = (base, totalMinutes, { min = 0, max = MAX_MINUTES } = {}) => {
  const clamped = Math.max(min, Math.min(max, totalMinutes));
  return dayjs(base)
    .hour(Math.floor(clamped / 60))
    .minute(clamped % 60)
    .second(0)
    .millisecond(0);
};

export const roundToNearestFive = (date) => {
  const total = snapToStep(minutesOf(date));
  return atMinutes(date, total);
};

const TimeUnit = ({
  value,
  min,
  max,
  onChange,
  onStep,
  caption,
  labelledBy,
  step = 1,
  disabled = false,
}) => {
  const [draft, setDraft] = useState(pad2(value));
  const justFocusedRef = useRef(false);

  useEffect(() => {
    setDraft(pad2(value));
  }, [value]);

  const commit = (raw) => {
    if (disabled) return;
    const parsed = parseInt(String(raw).replace(/\D/g, ''), 10);
    if (Number.isNaN(parsed)) {
      setDraft(pad2(value));
      return;
    }
    let next = Math.min(max, Math.max(min, parsed));
    if (step > 1) {
      next = snapToStep(next, step);
      next = Math.min(max, Math.max(min, next));
    }
    onChange(next);
    setDraft(pad2(next >= 60 && max >= 60 ? 0 : next));
  };

  return (
    <div className={styles.time_unit}>
      <div className={styles.time_unit_control}>
        <button
          type="button"
          className={styles.time_unit_step}
          onClick={() => !disabled && onStep(1)}
          aria-label="+1"
          disabled={disabled}
        >
          <ChevronUp size={18} strokeWidth={2.4} />
        </button>
        <div className={styles.time_unit_value}>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={draft}
            aria-labelledby={labelledBy}
            disabled={disabled}
            onChange={(event) => {
              const next = nextTimeDraft(event.target.value, draft, {
                justFocused: justFocusedRef.current,
              });
              justFocusedRef.current = false;
              setDraft(next);
              if (next.length === 2) commit(next);
            }}
            onBlur={() => commit(draft)}
            onFocus={(event) => {
              justFocusedRef.current = true;
              const target = event.target;
              window.requestAnimationFrame(() => target.select());
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                if (!disabled) onStep(1);
              }
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                if (!disabled) onStep(-1);
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                commit(draft);
              }
            }}
          />
        </div>
        <button
          type="button"
          className={styles.time_unit_step}
          onClick={() => !disabled && onStep(-1)}
          aria-label="-1"
          disabled={disabled}
        >
          <ChevronDown size={18} strokeWidth={2.4} />
        </button>
      </div>
      <span className={styles.time_unit_caption}>{caption}</span>
    </div>
  );
};

const TimeStepper = ({
  label,
  value,
  onChange,
  error,
  minTotal = 0,
  maxTotal = MAX_MINUTES,
  hourCaption = 'Ore',
  minuteCaption = 'Min',
  minuteStep = DEFAULT_MINUTE_STEP,
  disabled = false,
  compact = false,
}) => {
  const safeValue = value?.isValid?.() ? value : dayjs(value);
  const hours = safeValue.hour();
  const minutes = snapToStep(safeValue.minute(), minuteStep) % 60;
  const labelId = `time-stepper-${String(label || 'time')
    .replace(/\s+/g, '-')
    .toLowerCase()}`;

  const apply = (total) => {
    if (disabled) return;
    const snapped = snapToStep(total, minuteStep);
    const minSnapped = Math.ceil(minTotal / minuteStep) * minuteStep;
    const maxSnapped = Math.floor(maxTotal / minuteStep) * minuteStep;
    onChange(
      atMinutes(safeValue, Math.min(maxSnapped, Math.max(minSnapped, snapped)), {
        min: minSnapped,
        max: maxSnapped,
      }),
    );
  };

  return (
    <div
      className={clx(styles.time_picker, {
        [styles.time_picker_error]: !!error,
        [styles.time_picker_disabled]: disabled,
        [styles.time_picker_compact]: compact,
      })}
    >
      {label ? (
        <span id={labelId} className={styles.time_picker_label}>
          {label}
        </span>
      ) : null}
      <div className={styles.time_picker_units}>
        <TimeUnit
          value={hours}
          min={0}
          max={23}
          caption={hourCaption}
          labelledBy={labelId}
          disabled={disabled}
          onChange={(nextHour) => apply(nextHour * 60 + minutes)}
          onStep={(delta) => apply((hours + delta) * 60 + minutes)}
        />
        <span className={styles.time_picker_colon} aria-hidden="true">
          :
        </span>
        <TimeUnit
          value={minutes}
          min={0}
          max={60}
          step={minuteStep}
          caption={minuteCaption}
          labelledBy={labelId}
          disabled={disabled}
          onChange={(nextMinute) => {
            if (nextMinute >= 60) {
              apply((hours + 1) * 60);
              return;
            }
            apply(hours * 60 + nextMinute);
          }}
          onStep={(delta) => apply(hours * 60 + minutes + delta * minuteStep)}
        />
      </div>
      {error ? <span className={styles.time_picker_error_text}>{error}</span> : null}
    </div>
  );
};

export default TimeStepper;
